import { Button } from "@/components/ui/button";
import { Plus, LayoutTemplate } from "lucide-react";
import { useState } from "react";
import type { Section } from "./types";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableSection } from "./SortableSection";
import { toast } from "sonner";
import { TemplatePicker } from "./SectionTemplates/TemplatePicker";
type PageSectionsProps = {
  sections: Section[];
  onChange: (sections: Section[]) => void;
  isPro: boolean;
};
export const PageSections = ({
  sections,
  onChange,
  isPro
}: PageSectionsProps) => {
  const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState(false);
  const MAX_SECTIONS = 3;
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const addSection = () => {
    if (!isPro && sections.length >= MAX_SECTIONS) {
      toast.error(`Free Funnel is limited to ${MAX_SECTIONS} sections. Upgrade to add more!`);
      return;
    }
    const newSection: Section = {
      id: crypto.randomUUID(),
      layout: "1-col",
      theme: "light",
      blocks: []
    };
    onChange([...sections, newSection]);
  };
  const addTemplateSection = (template: any) => {
    if (!isPro && sections.length >= MAX_SECTIONS) {
      toast.error(`Free Funnel is limited to ${MAX_SECTIONS} sections. Upgrade to add more!`);
      return;
    }
    const newSection: Section = {
      id: crypto.randomUUID(),
      layout: "1-col",
      theme: "light",
      blocks: template.blocks.map((block: any) => ({
        ...block,
        id: crypto.randomUUID()
      }))
    };
    onChange([...sections, newSection]);
    toast.success("Template added successfully!");
  };
  const updateSection = (id: string, updatedSection: Section) => {
    onChange(sections.map(section => section.id === id ? updatedSection : section));
  };
  const removeSection = (id: string) => {
    onChange(sections.filter(section => section.id !== id));
  };
  const handleDragEnd = (event: any) => {
    const {
      active,
      over
    } = event;
    if (active.id !== over.id) {
      const oldIndex = sections.findIndex(section => section.id === active.id);
      const newIndex = sections.findIndex(section => section.id === over.id);
      onChange(arrayMove(sections, oldIndex, newIndex));
    }
  };
  return <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Page Sections</h3>
          <p className="text-sm text-muted-foreground">
            {isPro ? "Add as many sections as you need" : `${sections.length}/${MAX_SECTIONS} sections used`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsTemplatePickerOpen(true)} size="sm" variant="outline" className="rounded-full">
            <LayoutTemplate className="w-4 h-4 mr-2" />
            Use Template
          </Button>
          <Button onClick={addSection} size="sm" className="text-slate-50 border rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            Add Section
          </Button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map(section => section.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {sections.map(section => <SortableSection key={section.id} section={section} onUpdate={updatedSection => updateSection(section.id, updatedSection)} onDelete={() => removeSection(section.id)} />)}
          </div>
        </SortableContext>
      </DndContext>

      <TemplatePicker isOpen={isTemplatePickerOpen} onOpenChange={setIsTemplatePickerOpen} onSelectTemplate={addTemplateSection} />
    </div>;
};